/**
 * Administrator «Заявки» (iteration 2): VPP applications, newest first —
 * date, client with phone and email, object with SN and region, the 30-day
 * potential at the moment of applying. Table; on a phone a card per
 * application. No letter to the operator: the list is in the app.
 */
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { AdminApplicationRow, AdminApplicationsResponse } from 'shared';

import { useLocale } from '../../i18n/locale-context.js';
import type { Locale, MessageKey } from '../../i18n/messages.js';
import { api } from '../../lib/api-client.js';
import { countLabel, fill, formatDateTime, formatNumber, formatPhone, regionLabel } from './admin-format.js';
import {
  ADMIN_TOKENS,
  AdminPage,
  PHONE,
  QueryState,
  StateCard,
  cardSx,
  subSx,
  useIsAdmin,
} from './admin-ui.js';

const T = ADMIN_TOKENS;

const COLUMNS: MessageKey[] = [
  'operator.applications.col.date',
  'operator.applications.col.client',
  'operator.applications.col.site',
  'operator.applications.col.potential',
];

const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
  '& th': {
    textAlign: 'left',
    fontWeight: 500,
    color: T.slate,
    fontSize: 12.5,
    p: '0 14px 8px 0',
    whiteSpace: 'nowrap',
  },
  '& td': { borderTop: `1px solid ${T.line}`, p: '11px 14px 11px 0', verticalAlign: 'top' },
  '& td:first-of-type': { whiteSpace: 'nowrap' },
  '& td .sub': { ...subSx, display: 'block', fontSize: 12.5, mt: '2px' },
  '& .num': { fontVariantNumeric: 'tabular-nums' },
  '& a': { color: T.navy },
  [PHONE]: {
    '& thead': { display: 'none' },
    '&, & tbody, & tr, & td': { display: 'block' },
    '& tr': { borderTop: `1px solid ${T.line}`, py: '10px' },
    '& tr:first-of-type': { borderTop: 0 },
    '& td': { border: 0, p: '3px 0', overflowWrap: 'anywhere' },
    '& td[data-k]::before': {
      content: 'attr(data-k)',
      display: 'block',
      fontSize: 12,
      color: T.slate,
    },
  },
} as const;

export function OperatorApplicationsPage() {
  const { t, locale } = useLocale();
  const isAdmin = useIsAdmin();
  const query = useQuery({
    queryKey: ['admin', 'applications'],
    queryFn: () => api.get<AdminApplicationsResponse>('/api/admin/applications'),
    enabled: isAdmin,
  });
  const applications = query.data?.applications;

  return (
    <AdminPage
      title={t('operator.applications.heading')}
      sub={
        applications?.length
          ? countLabel(t, locale, 'operator.count.applications', applications.length)
          : null
      }
    >
      {!applications ? (
        <QueryState error={query.error} onRetry={() => query.refetch()} />
      ) : applications.length === 0 ? (
        <StateCard
          title={t('operator.applications.empty.title')}
          text={t('operator.applications.empty.text')}
        />
      ) : (
        <Box component="section" sx={cardSx}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  {COLUMNS.map((k) => (
                    <th key={k}>{t(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <ApplicationRow key={a.id} application={a} />
                ))}
              </tbody>
            </Box>
          </Box>
        </Box>
      )}
    </AdminPage>
  );
}

function ApplicationRow({ application: a }: { application: AdminApplicationRow }) {
  const { t, locale } = useLocale();
  const [date, client, site, potential] = COLUMNS.map((k) => t(k));
  // Accounts registered before iteration 2 have no name: the email takes its line.
  const contacts = [
    a.client.phone ? formatPhone(a.client.phone) : null,
    a.client.name ? a.client.email : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const where = [
    fill(t('operator.sn'), { sn: a.site.device_sn }),
    a.site.region ? regionLabel(a.site.region, locale) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <tr>
      <td data-k={date} className="num">
        {formatDateTime(a.created_at)}
      </td>
      <td data-k={client}>
        {a.client.name ?? a.client.email}
        {contacts ? <span className="sub num">{contacts}</span> : null}
      </td>
      <td data-k={site}>
        <Link to={`/app/sites/${a.site.id}`}>{a.site.name}</Link>
        <span className="sub num">{where}</span>
      </td>
      <td data-k={potential} className="num">
        {potentialText(a, t, locale)}
      </td>
    </tr>
  );
}

/** «3 914 ₴» or a short reason why there is no amount. */
function potentialText(a: AdminApplicationRow, t: (k: MessageKey) => string, locale: Locale): string {
  const p = a.potential;
  if (!p) return t('operator.applications.potential.none');
  if (p.status === 'no_osr_rate') return t('operator.applications.potential.no_osr_rate');
  if (p.status === 'no_data' || p.increment_window === null)
    return t('operator.applications.potential.no_data');
  return `${formatNumber(p.increment_window, locale)} ₴`;
}
